/**
 * Midden van werkplek v6 (ontwerp 26, sessie 67): de kop staat stil,
 * daaronder één pagina die scrolt met de berichten en helemaal onderaan
 * het antwoordvak. Het antwoordvak groeit mee en scrolt nooit zelf.
 *
 * iPhone: het midden is verborgen tot er een zaak open is
 * (`.v6-vandaag.zaak-open`); dan vult het het scherm, met terugpijl en
 * de knoppen Klant en Copiloot in de kop. De voet van het antwoordvak
 * met de hoofdknop staat daar vast onderaan (sessie 70, `voetplek.tsx`).
 */
import { ZONDER_CONCEPT, zaakUitKaart } from "../zaakUitKaart";
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

import { v6Api, type AfrondUitkomst, type V6Zaak } from "../api";
import { useV6 } from "../context";
import { useLive, useLiveEvent } from "../live";
import { beginMeting, laatsteAanvraag, meetNaAanvraag, rondMetingAf, type LopendeMeting } from "../meting";
import { afrondingVanReden, kaartUitCache, naAfronden, naTerugzetten } from "../naActie";
import { v6Sleutels } from "../sleutels";
import { POLL_DICHT_MS } from "../vangnet";
import { Berichten, useLiveRegels } from "./Berichten";
import { Kop } from "./Kop";
import { Meldingbalk, type MiddenMelding } from "./Melding";
import { useMidden } from "./middenQuery";
import { Overdracht } from "./Overdracht";
import { RetourlabelZaak } from "./RetourlabelZaak";
import { Antwoordvak } from "./Standen";
import { useNu } from "./tijd";
import { useVerzendVolger } from "./verzendVolger";
import { VerzendRegels } from "./verzending";
import { useSmal, VoetPlekProvider } from "./voetplek";
import "./midden.css";
import "./midden-m7.css";

/** Het concept alvast ophalen, naast de zaak in plaats van erna. */
function haalConceptAlvast(qc: QueryClient, zaakId: string): void {
  const kaart = kaartUitCache(qc, zaakId);
  if (!kaart || ZONDER_CONCEPT.has(kaart.groep)) return;
  void qc.prefetchQuery({
    queryKey: v6Sleutels.concept(zaakId),
    queryFn: () => v6Api.concept(zaakId),
  });
}

export interface MiddenProps {
  /** iPhone: terug naar de lijst. */
  onTerug: () => void;
}

export type ToonMelding = (melding: Omit<MiddenMelding, "id" | "zaakId">) => void;

export function Midden({ onTerug }: MiddenProps) {
  const { zaakId } = useV6();
  const queryClient = useQueryClient();
  const [melding, setMelding] = useState<MiddenMelding | null>(null);
  const volg = useRef(0);
  const paginaRef = useRef<HTMLDivElement>(null);
  const smal = useSmal();
  const [regelPlek, setRegelPlek] = useState<HTMLDivElement | null>(null);
  const [voetPlek, setVoetPlek] = useState<HTMLDivElement | null>(null);
  const [balkPlek, setBalkPlek] = useState<HTMLDivElement | null>(null);
  const plek = useMemo(
    () =>
      smal
        ? { regel: regelPlek, voet: voetPlek, balk: null }
        : { regel: null, voet: null, balk: balkPlek },
    [smal, regelPlek, voetPlek, balkPlek],
  );

  // Sessie 70 punt 5: terug naar de lijst zet de pagina bovenaan. Het
  // midden blijft op de iPhone staan (verborgen); opent JP dezelfde zaak
  // opnieuw, dan hield WebKit de oude scrollstand vast en stond
  // "Eerdere gesprekken" buiten beeld.
  const terug = useCallback(() => {
    if (paginaRef.current) paginaRef.current.scrollTop = 0;
    onTerug();
  }, [onTerug]);

  const { verbonden } = useLive();
  useVerzendVolger();

  // Sessie 72 (zaak openen onder 1 s): het concept gaat tegelijk met de
  // zaak op pad, en tot de zaak er is staat de kaart uit de lijst in beeld.
  const meting = useRef<{ zaakId: string; meting: LopendeMeting } | null>(null);
  useEffect(() => {
    if (!zaakId) return;
    meting.current = { zaakId, meting: beginMeting("zaak_openen") };
    haalConceptAlvast(queryClient, zaakId);
  }, [zaakId, queryClient]);

  const zaakQuery = useQuery({
    queryKey: v6Sleutels.zaak(zaakId ?? ""),
    queryFn: () => v6Api.zaak(zaakId as string),
    enabled: Boolean(zaakId),
    placeholderData: () => {
      const kaart = zaakId ? kaartUitCache(queryClient, zaakId) : null;
      return kaart ? zaakUitKaart(kaart) : undefined;
    },
    // Geen vangnet voor de zaak: die volgt de live-stroom; alleen als de
    // stroom dicht is elke minuut (vangnet.ts).
    refetchInterval: verbonden ? false : POLL_DICHT_MS,
  });
  const zaak = zaakQuery.data?.zaak_id === zaakId ? zaakQuery.data : undefined;
  const laadt = Boolean(zaak) && zaakQuery.isPlaceholderData;

  // De echte zaak staat in beeld: meting "zaak openen" rond.
  useEffect(() => {
    const lopend = meting.current;
    if (!zaak || laadt || !lopend || lopend.zaakId !== zaak.zaak_id) return;
    meting.current = null;
    const aanvraag = laatsteAanvraag(/\/api\/werkplek\/v6\/zaken\/[0-9a-f-]{36}$/);
    const zaakMs =
      aanvraag && aanvraag.startTime >= lopend.meting.begin - 50
        ? aanvraag.responseEnd - aanvraag.startTime
        : 0;
    rondMetingAf(lopend.meting, { zaak: zaakMs });
  }, [zaak, laadt]);

  // Live: een nieuw bericht in dit gesprek ververst de zaak. Typen niet;
  // dat toont de live-stand zelf.
  useLiveEvent((event) => {
    if (!zaakId || event.soort === "typen") return;
    const vanDezeZaak =
      event.zaak_id === zaakId || Boolean(zaak?.gesprek_ids.includes(event.gesprek_id));
    if (vanDezeZaak) void queryClient.invalidateQueries({ queryKey: v6Sleutels.zaak(zaakId) });
  });

  const toonMelding = useCallback<ToonMelding>(
    (m) => {
      if (!zaakId) return;
      volg.current += 1;
      setMelding({ ...m, id: volg.current, zaakId });
    },
    [zaakId],
  );

  // Sessie 72: na Sluiten, Spam of Verwijderen alleen de kaart, de zaak en
  // de tellers; niet meer alles onder ["v6"] opnieuw.
  const kanaal = zaak?.kanaal ?? null;
  const naAfgerond = useCallback(
    (tekst: string, uitkomst: AfrondUitkomst) => {
      const eventId = uitkomst.event_id;
      const id = uitkomst.zaak_id;
      void naAfronden(queryClient, id, afrondingVanReden(uitkomst.reden), kanaal);
      meetNaAanvraag("sluiten", /\/afronden$/);
      toonMelding({
        tekst,
        tot: uitkomst.ongedaan_tot,
        ongedaan: eventId
          ? async () => {
              await v6Api.ongedaan(id, eventId);
              naTerugzetten(queryClient, id);
            }
          : null,
      });
    },
    [queryClient, toonMelding, kanaal],
  );

  if (!zaakId) {
    // Keuze 7 van 75: na het laatste antwoord is er niets meer voor JP; de
    // melding met Ongedaan maken blijft dan hier staan.
    const vorige = melding?.blijft ? melding : null;
    return (
      <section className="v6-vlak v6-midden leeg" aria-label="Gesprek en antwoord">
        {vorige && (
          <Meldingbalk key={vorige.id} melding={vorige} onKlaar={() => setMelding(null)} />
        )}
        <p className="v6-leeg">{vorige ? "Niets meer voor jou." : "Kies een zaak in de lijst."}</p>
      </section>
    );
  }

  if (!zaak) {
    return (
      <section className="v6-vlak v6-midden leeg" aria-label="Gesprek en antwoord">
        {zaakQuery.isError ? (
          <div className="v6-leeg">
            <p className="v6-fout" role="alert">
              Deze zaak kon niet geladen worden.
            </p>
            <button type="button" className="v6-knop" onClick={() => void zaakQuery.refetch()}>
              Opnieuw
            </button>
          </div>
        ) : (
          <p className="v6-leeg">Laden</p>
        )}
      </section>
    );
  }

  const zichtbareMelding = melding && (melding.zaakId === zaakId || melding.blijft) ? melding : null;

  return (
    <section className="v6-vlak v6-midden" aria-label="Gesprek en antwoord">
      {/* Per zaak opnieuw: fouten en bezig-standen lopen niet mee naar de volgende zaak. */}
      <Kop key={zaak.zaak_id} zaak={zaak} onTerug={terug} onAfgerond={naAfgerond} />
      {zichtbareMelding && (
        <Meldingbalk
          key={zichtbareMelding.id}
          melding={zichtbareMelding}
          onKlaar={() => setMelding(null)}
        />
      )}
      <VoetPlekProvider value={plek}>
        <Pagina zaak={zaak} paginaRef={paginaRef} laadt={laadt}>
          {laadt ? (
            <div className="antwoord" aria-busy="true">
              <p className="v6-leeg">Antwoord laden</p>
            </div>
          ) : (
            <Antwoordvak key={zaak.zaak_id} zaak={zaak} toonMelding={toonMelding} />
          )}
        </Pagina>
      </VoetPlekProvider>
      {!smal && <div className="m7-balkplek" ref={setBalkPlek} data-testid="m7-balkplek" />}
      {smal && (
        <div className="antwoord vasteplek" data-testid="v6-vasteplek">
          <div ref={setRegelPlek} />
          <div ref={setVoetPlek} />
        </div>
      )}
    </section>
  );
}

/** Pixels vanaf de onderkant die nog als "onderaan" tellen. */
const ONDERAAN_PX = 80;

/**
 * De ene pagina die scrolt. Bij openen van een zaak staat hij bovenaan
 * (zoals de mock); komt er live iets bij en stond je onderaan, dan
 * scrolt hij mee.
 */
function Pagina({
  zaak,
  paginaRef: ref,
  laadt = false,
  children,
}: {
  zaak: V6Zaak;
  paginaRef: RefObject<HTMLDivElement>;
  /** De zaak komt nog (kop uit de lijstkaart): berichten laden. */
  laadt?: boolean;
  children: ReactNode;
}) {
  const nu = useNu(10_000);
  const { typt, novaBezig } = useLiveRegels(zaak, nu);
  const onderaan = useRef(false);
  const vorige = useRef<{ zaakId: string | null; aantal: number }>({ zaakId: null, aantal: 0 });
  const open = zaak.stand !== "afgerond";
  const aantal = zaak.berichten.length + (typt ? 1 : 0) + (novaBezig ? 1 : 0);
  // Motor 7 (keuze 6 van 75): het overdrachtsblok boven het gesprek.
  const { data: midden } = useMidden(open && !laadt ? zaak.zaak_id : null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (vorige.current.zaakId !== zaak.zaak_id) {
      el.scrollTop = 0;
      onderaan.current = false;
    } else if (aantal > vorige.current.aantal && onderaan.current) {
      el.scrollTop = el.scrollHeight;
    }
    vorige.current = { zaakId: zaak.zaak_id, aantal };
  }, [zaak.zaak_id, aantal, ref]);

  const opScroll = () => {
    const el = ref.current;
    if (!el) return;
    onderaan.current = el.scrollHeight - el.scrollTop - el.clientHeight < ONDERAAN_PX;
  };

  return (
    <div className="pagina" ref={ref} onScroll={opScroll} data-testid="v6-pagina">
      {laadt ? (
        <p className="v6-leeg" aria-busy="true">
          Berichten laden
        </p>
      ) : (
        <>
          {/* Sessie 171: retourlabel in één tik, op de laatste bestelling. */}
          <RetourlabelZaak key={`retour-${zaak.zaak_id}`} zaak={zaak} />
          <Overdracht key={zaak.zaak_id} midden={midden} />
          <Berichten zaak={zaak} typt={typt} novaBezig={novaBezig} />
        </>
      )}
      <VerzendRegels zaakId={zaak.zaak_id} gepland={zaak.status_woord.startsWith("Gepland")} />
      {children}
    </div>
  );
}
